import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, Loader2, ArrowRight, BookOpen, Clock } from "lucide-react";
import { toast } from "sonner";
import Highlight from "@/components/Highlight";

export default function BibleSearch() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("scripture"); // "scripture" | "studies"
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [scripture, setScripture] = useState(null); // {mode, results}
  const [studies, setStudies] = useState(null);
  const [opening, setOpening] = useState("");
  const [translation, setTranslation] = useState("niv");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setTranslation(data.translation || "niv")).catch(() => {});
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (!query) return;
    setLoading(true);
    try {
      if (tab === "scripture") {
        const { data } = await api.get("/bible/search", { params: { query } });
        setScripture(data);
      } else {
        const { data } = await api.get("/search/studies", { params: { query } });
        setStudies(data.results || []);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Search failed");
    }
    setLoading(false);
  };

  const openPassage = async (ref) => {
    setOpening(ref);
    try {
      const { data } = await api.post("/study/start", { input: ref, mode: "passage", translation });
      navigate(`/study/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't open that passage");
      setOpening("");
    }
  };

  const switchTab = (t) => {
    setTab(t);
    setScripture(null);
    setStudies(null);
  };

  return (
    <div className="px-6 py-10 space-y-8 md:max-w-2xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header>
        <div className="text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF] mb-2">Search</div>
        <h1 className="text-3xl text-white leading-tight">Find it in the Word.</h1>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Search Scripture for a word or phrase, or look back through your own studies.
        </p>
      </header>

      {/* Tabs */}
      <div className="grid grid-cols-2 gap-2 p-1 bg-[#141B29] rounded-xl">
        {[["scripture", "Scripture", BookOpen], ["studies", "My studies", Clock]].map(([id, label, Icon]) => (
          <button
            key={id}
            data-testid={`search-tab-${id}`}
            onClick={() => switchTab(id)}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              tab === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
            }`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
          </button>
        ))}
      </div>

      <form onSubmit={run} className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={1.5} />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "scripture" ? "e.g. love, John 3:16, mustard seed" : "e.g. patience, Romans, forgiveness"}
            className="h-12 pl-11 rounded-xl border-[#29344D] bg-[#0E131F]"
          />
        </div>
        <button
          data-testid="search-submit"
          type="submit"
          disabled={loading}
          className="rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-5 text-sm font-medium transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </button>
      </form>

      {/* Scripture results */}
      {tab === "scripture" && scripture && (
        <div className="space-y-3" data-testid="search-scripture-results">
          {scripture.results?.length === 0 && (
            <p className="text-sm text-slate-500">Nothing came up. Try another word, or a reference like “Psalm 23”.</p>
          )}
          {(scripture.results || []).map((r, i) => (
            <button
              key={i}
              data-testid={`search-verse-${i}`}
              onClick={() => openPassage(r.reference)}
              disabled={!!opening}
              className="group w-full text-left rounded-2xl border border-[#1E273D] bg-[#0E131F] hover:border-[#00E5FF]/40 p-5 transition-colors disabled:opacity-60"
            >
              <div className="flex items-center justify-between">
                <span className="text-lg text-[#00E5FF]">{r.reference}</span>
                {opening === r.reference ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" strokeWidth={1.5} />
                ) : (
                  <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#00E5FF] transition-colors" strokeWidth={1.5} />
                )}
              </div>
              <p className="text-base text-slate-300 mt-1 leading-relaxed line-clamp-3">
                <Highlight text={r.text} query={scripture.mode === "keyword" ? q : ""} />
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Studies results */}
      {tab === "studies" && studies && (
        <div className="space-y-3" data-testid="search-studies-results">
          {studies.length === 0 && (
            <p className="text-sm text-slate-500">Nothing here yet. Once you sit with a passage, it'll be searchable right here.</p>
          )}
          {studies.map((s) => (
            <button
              key={s.id}
              data-testid={`search-study-${s.id}`}
              onClick={() => navigate(`/study/${s.id}`)}
              className="group w-full text-left rounded-2xl border border-[#1E273D] bg-[#0E131F] hover:border-[#00E5FF]/40 p-5 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-lg text-slate-200">{s.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#00E5FF] transition-colors" strokeWidth={1.5} />
              </div>
              {s.reference && <div className="text-xs uppercase tracking-widest text-[#00E5FF] mt-1">{s.reference}</div>}
              {s.snippet && <p className="text-sm text-slate-400 mt-1.5 leading-relaxed line-clamp-2">{s.snippet}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
